import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, ShoppingCart, Trash2, Check } from "lucide-react";
import { ITEM_CATS } from "@/components/PetItems";
import { api, assetUrl, errMsg, fmtAgo } from "@/lib/api";

function Row({ s, onToggle, onDelete }) {
  const c = ITEM_CATS[s.category] || null;
  return (
    <div className={`pl-card p-3 flex items-center gap-3 ${s.done ? "bg-[#F3FBF7] border-[#BBF0D9]" : ""}`} data-testid={`shop-item-${s.id}`}>
      <button onClick={() => onToggle(s)} aria-label={s.done ? `Mark ${s.name} not bought` : `Mark ${s.name} bought`} data-testid={`shop-item-toggle-${s.id}`}
        className={`h-12 w-12 shrink-0 rounded-full grid place-items-center active:scale-90 transition-transform ${s.done ? "bg-[#10B981] text-white" : "border-[3px] border-dashed border-[#F59E0B] bg-[#FFF7E6] text-[#F59E0B]"}`}>
        <Check className="h-6 w-6" strokeWidth={3} />
      </button>
      {s.photo_url ? <img src={assetUrl(s.photo_url)} alt={s.name} className="h-12 w-12 rounded-xl object-cover shrink-0" />
        : c && <span className="h-12 w-12 rounded-xl grid place-items-center shrink-0" style={{ background: c.bg, color: c.color }}><c.icon className="h-5 w-5" /></span>}
      <div className="flex-1 min-w-0">
        <p data-testid={`shop-item-name-${s.id}`} className={`font-semibold truncate ${s.done ? "line-through text-slate-400" : ""}`}>{s.name}</p>
        <p data-testid={`shop-item-meta-${s.id}`} className="text-xs text-slate-500">
          {s.pet_name ? `${s.pet_name} · ` : ""}{s.done ? `Bought by ${s.done_by} · ${fmtAgo(s.done_at)}` : `Added by ${s.added_by} · ${fmtAgo(s.created_at)}`}
        </p>
      </div>
      <button onClick={() => onDelete(s)} aria-label={`Remove ${s.name}`} data-testid={`shop-item-delete-${s.id}`} className="h-10 w-10 grid place-items-center rounded-full text-slate-400 hover:bg-slate-100"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export default function Shopping() {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get("/shopping").then((r) => { setRows(r.data); setFailed(false); }).catch(() => setFailed(true)), []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  const run = (fn) => async (...a) => { try { await fn(...a); } catch (err) { toast.error(errMsg(err)); } load(); };
  const add = run(async (e) => { e.preventDefault(); await api.post("/shopping", { name }); setName(""); });
  const toggle = run((s) => api.post(`/shopping/${s.id}/toggle`));
  const remove = run(async (s) => { if (window.confirm(`Remove ${s.name} from the list?`)) await api.delete(`/shopping/${s.id}`); });
  const clear = run(() => api.post("/shopping/clear-done"));
  if (!rows && failed) return <div className="pl-card p-6 text-center" data-testid="shop-load-error"><p className="mb-3">Couldn't load the list.</p><button className="pl-btn-ghost" onClick={load} data-testid="shop-retry-btn">Try again</button></div>;
  if (!rows) return <div className="pl-card h-40 animate-pulse" />;
  const open = rows.filter((r) => !r.done);
  const done = rows.filter((r) => r.done);
  return (
    <div data-testid="shopping-page">
      <h1 className="font-heading text-4xl font-extrabold flex items-center gap-2"><ShoppingCart className="h-8 w-8 text-[#D97706]" />Running low</h1>
      <p className="text-sm text-slate-500 mt-1 mb-5">One shared list for the whole household. Tap "Running low" on any pet favorite, or type it here.</p>
      <form onSubmit={add} className="flex gap-2 mb-5">
        <input className="pl-input" placeholder="e.g. Cat litter, poop bags" value={name} onChange={(e) => setName(e.target.value)} data-testid="shop-add-input" />
        <button disabled={!name.trim()} className="pl-btn-primary shrink-0" data-testid="shop-add-btn"><Plus className="h-4 w-4" />Add</button>
      </form>
      <div className="space-y-3" data-testid="shop-open-list">{open.map((s) => <Row key={s.id} s={s} onToggle={toggle} onDelete={remove} />)}</div>
      {open.length === 0 && <p className="text-slate-500 text-center py-6" data-testid="shop-empty">All stocked up. Nothing to buy.</p>}
      {done.length > 0 && (
        <>
          <div className="flex items-center justify-between mt-8 mb-3">
            <h2 className="font-heading text-lg font-semibold text-slate-500">Bought</h2>
            <button onClick={clear} className="text-sm font-semibold text-slate-500" data-testid="shop-clear-done-btn">Clear bought</button>
          </div>
          <div className="space-y-3" data-testid="shop-done-list">{done.map((s) => <Row key={s.id} s={s} onToggle={toggle} onDelete={remove} />)}</div>
        </>
      )}
    </div>
  );
}
